import clsx from "clsx";
import Link from "next/link";
import { useSelector } from "react-redux";
import { isPast } from "date-fns";
import { useRouter } from "next/router";

import { Container, Skeleton } from "@chef/components";
import { BRAND_PATHS, ORDER_STATUS_IDS, ORDER_TYPE_IDS } from "@chef/constants";
import { selectNudges, useOrdersQuery } from "@chef/state-management";
import {
  formatDate,
  isMealboxBasketType,
  orderHasBeenDelivered,
} from "@chef/helpers";
import { useCalendarData } from "@chef/state-management/hooks";
import { Nudge } from "@chef/icons/small";
import {
  IPromotion,
  IWeekOption,
  usePromotionData,
  useWeekOptionsData,
} from "@chef/feature-sanity";
import { isEqualStrings } from "@chef/utils/equal";

import { shouldShowDelivery } from "@chef/helpers";

import { intl } from "./WeeksNavigation.Intl";

interface WeeksNavigationProps {
  week: number;
  year: number;
  sticky?: boolean;
  promotion: IPromotion | null;
  weekOptions?: IWeekOption[];
}

interface NoDeliveryWeekProps {
  date: string;
  isActive: boolean;
  undeliverable: boolean;
}

interface PlannedDeliveryWeekProps {
  day: string;
  date: string;
}

const NoDeliveryWeek = ({
  date,
  isActive,
  undeliverable,
}: NoDeliveryWeekProps) => {
  const { CANCELLED, CLOSED } = intl;

  return (
    <div>
      <p className={clsx(!isActive && "text-error group-hover:text-white")}>
        {undeliverable ? CLOSED : CANCELLED}
      </p>
      <strong
        className={clsx(!isActive && "text-grey-1 group-hover:text-white")}
      >
        {date}
      </strong>
    </div>
  );
};

const CurrentOrPreviousDeliveryWeek = () => {
  const { pathname } = useRouter();
  const isActive = isEqualStrings(pathname, BRAND_PATHS.MY_DELIVERY);
  const { order } = useOrdersQuery(
    {
      count: 1,
      orderTypes: [ORDER_TYPE_IDS.RECURRING],
      orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
    },
    {
      selectFromResult: (result) => ({
        order: result.data?.orders?.[0],
      }),
    },
  );

  if (!order) {
    return null;
  }

  const showDelivery = shouldShowDelivery(order.deliveryDate);

  if (!showDelivery) {
    return null;
  }

  const hasBeenDelivered = orderHasBeenDelivered(order);
  const deliveryStatusMessage = hasBeenDelivered
    ? intl.DELIVERED
    : intl.IN_TRANSIT;
  const deliveryDate = formatDate(order.deliveryDate, intl.SHORT_DATE_FORMAT);

  return (
    <Link
      key={order.orderId}
      href={BRAND_PATHS.MY_DELIVERY}
      passHref
      className={clsx(
        "flex items-center justify-center h-12 w-20 md:w-22 text-sm text-center relative hover:bg-primary hover:text-white rounded",
        isActive && "bg-primary text-white",
      )}
      id={order.orderId.toString()}
    >
      <div className="group-hover:text-white">
        <div className="flex items-center justify-center gap-2">
          <p>{deliveryStatusMessage}</p>
        </div>
        <strong>{deliveryDate}</strong>
      </div>
    </Link>
  );
};

const PlannedDeliveryWeek = ({ day, date }: PlannedDeliveryWeekProps) => {
  return (
    <div className="group-hover:text-white">
      <p>{day}</p>
      <strong>{date}</strong>
    </div>
  );
};

export const WeeksNavigation = ({
  week,
  year,
  sticky = false,
  promotion,
  weekOptions: _weekOptions,
}: WeeksNavigationProps) => {
  const { pathname } = useRouter();
  const isOnMyMenuPage = pathname.includes(BRAND_PATHS.MY_MENU_PAGE);
  const { calendar } = useCalendarData({
    week,
    year,
  });

  const { isLoading: orderIsLoading } = useOrdersQuery({
    count: 1,
    orderTypes: [ORDER_TYPE_IDS.RECURRING],
    orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
  });

  const [promotionData] = usePromotionData(promotion);
  const [weeklyPromotions] = useWeekOptionsData(_weekOptions);

  const isAfterCutoffPromotion = promotionData
    ? isPast(new Date(promotionData.cutoffDate))
    : false;

  const nudges = useSelector(selectNudges);

  if (!calendar || orderIsLoading) {
    return (
      <div className="flex items-center justify-center w-full h-12 mt-3 mb-2 space-x-2">
        {[...Array(4)].map((_, index) => (
          <div key={`skeleton-${index}`} className="w-20 h-12 md:w-22">
            <Skeleton height={12} animation="pulse" variant="rounded" />
          </div>
        ))}
      </div>
    );
  }

  const weekArray = calendar
    .filter(Boolean)
    .map((w) => {
      const options = weeklyPromotions?.find((p) => {
        if (!p.displayRange) {
          return false;
        }

        const [start, end, weeks] = p.displayRange.split("..");

        const now = new Date();

        const startDate = new Date(start);
        const endDate = new Date(end);
        const weekNumbers = weeks.split(",").map((w) => +w);

        return (
          now >= startDate && now <= endDate && weekNumbers.includes(w.week)
        );
      });

      const dt = new Date(
        w.baskets.find((b) => b.week === w.week && b.year === w.year)
          ?.shippingDetails.plannedDeliveryInformation.deliveryDate || 0,
      );

      const showNudge = options?.showNudge && +week !== +w.week;

      const showSeasonalCampaignNudge =
        !isAfterCutoffPromotion &&
        promotionData?.week === w.week &&
        promotionData?.year === w.year &&
        promotionData?.showDot === true;

      const isPaused = !w.baskets.find(
        (b) => b.week === w.week && b.year === w.year && isMealboxBasketType(b),
      )?.shippingDetails?.hasDelivery;

      const isBeforeStartDate = !w.isBeforeStartDateWeek;

      const undeliverable = !w.baskets.find(
        (b) => b.week === w.week && b.year === w.year && isMealboxBasketType(b),
      )?.shippingDetails.plannedDeliveryInformation.isAvailableForDelivery;

      return {
        week: w.week,
        year: w.year,
        weekObj: w,
        options,
        dt,
        display: {
          date: formatDate(dt, intl.SHORT_DATE_FORMAT),
          day: formatDate(dt, "eeee"),
          showNudge,
          isPaused,
          undeliverable,
          isBeforeStartDate,
          showSeasonalCampaignNudge,
        },
      };
    })
    .filter(Boolean)
    .slice(0, 3);

  return (
    <Container
      outerClassName={clsx(
        "bg-background shadow-sm z-40 w-full pt-1",
        sticky && "sticky top-0",
      )}
      innerClassName="flex justify-between items-center"
    >
      <div
        className="flex items-center justify-center w-full h-full p-2 space-x-2"
        id="weeks-navigation-menu"
      >
        <CurrentOrPreviousDeliveryWeek />

        {weekArray.map((cd, i) => {
          const {
            date,
            showNudge,
            showSeasonalCampaignNudge,
            isPaused,
            undeliverable,
            day,
            isBeforeStartDate,
          } = cd.display;

          const isActive = +cd.week === +week && isOnMyMenuPage;

          const nudge = nudges.find((n) => n.week === cd.week);

          const shouldShowNudge =
            showSeasonalCampaignNudge || (showNudge && !nudge?.seen);

          const noDeliveryWeek =
            isPaused || undeliverable || !isBeforeStartDate;

          return (
            <Link
              key={i}
              href={BRAND_PATHS.MY_MENU_WEEK_HREF({
                week: cd.week,
                year: cd.year,
              })}
              passHref
              className={clsx(
                "group flex items-center justify-center h-12 w-20 md:w-22  text-sm text-center relative hover:bg-primary rounded",
                isActive && "bg-primary text-white",
              )}
              id={i.toString()}
            >
              {noDeliveryWeek ? (
                <NoDeliveryWeek
                  date={date}
                  isActive={isActive}
                  undeliverable={undeliverable}
                />
              ) : (
                <PlannedDeliveryWeek day={day} date={date} />
              )}
              {shouldShowNudge &&
                (showSeasonalCampaignNudge ? (
                  <div className="absolute w-3 h-3 rounded-full -right-1 -top-1 bg-secondary z-1" />
                ) : (
                  <Nudge className="absolute -top-1 -right-1 z-1" />
                ))}
            </Link>
          );
        })}
      </div>
    </Container>
  );
};
